import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import LinearProgress from '@material-ui/core/LinearProgress';
import { ColumnApi, GridApi } from 'ag-grid-community';

import {
    queueMapFitToExtent,
    resetLevel,
    stopFetchingCollection,
} from '../../../../state/_actions';

import { createCellInteractionHandler } from './selectors';

import { IOwnProps } from './types';
import { useStyles } from './Themable.hooks';

import SourceSetGrid from '../../../../components/SourceSetGrid';
import AnalyticsGrid from '../AnalyticsGrid';
import EditOverlay from '../../../../components/EditOverlay';
import SourceSetGridChart from '../SourceSetGridChart';

import { responseMessage } from '../../../../helpers/responseMessage';
import TranslationHelper from '../../../../helpers/TranslationHelper';
import { useSourceSetModel } from '../../selectors/composed/index.hooks';
import {
    useIsAnalyticsModeEnabled,
    useIsGridChart,
    useSourceSetId,
} from '../../selectors/index.hooks';
import { useFetching } from '../../selectors/feching/index.hooks';

import DataGridPaneHeader from './DataGridPaneHeader';

const DataGridPane = ({
    sourceSet,
    creatorLevel,
    timeLocked,
    layout,
    selectedSourceSetElementId,
    customComponents,
}: IOwnProps) => {
    const dispatch = useDispatch();

    const sourceSetModel = useSourceSetModel();
    const isChart = useIsGridChart();
    const fetching = useFetching();
    const sourceSetId = useSourceSetId();
    const isAnalyticsEnabled = useIsAnalyticsModeEnabled();

    const { enqueueSnackbar } = useSnackbar();
    const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
    const [gridColumnApi, setGridColumnApi] = useState<ColumnApi | undefined>(
        undefined
    );
    const [isFetching, setIsFetching] = useState(false);
    const [filter, setFilter] = useState('');
    const classes = useStyles();

    const cellInteractionHandler = createCellInteractionHandler(dispatch, {
        sourceSet,
        creatorLevel,
        timeLocked,
        selectedSourceSetElementId,
    } as IOwnProps);

    const handleClearClick = () => {
        if (sourceSetId !== 'lastStates') {
            dispatch(stopFetchingCollection(sourceSetId || ''));
        }
        dispatch(resetLevel(creatorLevel));
        dispatch(queueMapFitToExtent());
    };

    const showError = (errorCode: number = 0) => {
        const message = responseMessage(errorCode);
        enqueueSnackbar(TranslationHelper.translate(message.text), {
            variant: message.type,
        });
        handleClearClick();
    };

    const handleFilterModelChange = () => {
        if (sourceSetModel) {
            setFilter(sourceSetModel.quickFilter);
        } else {
            setFilter('');
        }
    };

    useEffect(() => {
        handleFilterModelChange();
    }, [sourceSetModel]);

    useEffect(() => {
        const sourceSetName =
            sourceSetId && fetching[sourceSetId + 'Fetching']
                ? sourceSetId + 'Fetching'
                : 'sourceSetFetching';

        const fetchData = fetching[sourceSetName] || null;
        const nowFetching = fetchData ? fetchData.isFetching : false;

        if (fetchData?.error) {
            console.error('Fetch data error', fetchData.error);
            showError(fetchData.error.status);
        }

        if (isFetching !== nowFetching) {
            setIsFetching(nowFetching);
        }
    }, [fetching, sourceSetId, isFetching]);

    useEffect(() => {
        if (sourceSetId !== 'lastStates') {
            dispatch(stopFetchingCollection('lastStates'));
        }
        handleFilterModelChange();
    }, [sourceSetId, dispatch]);

    useEffect(() => {
        if (isChart) {
            setGridApi(undefined);
            setGridColumnApi(undefined);
        }
    }, [isChart, layout]);

    const handleChangeFilter = (filter: string) => {
        setFilter(filter);
    };
    return (
        <Paper className={classes.pane}>
            <div className={classes.wrapper}>
                <DataGridPaneHeader
                    gridApi={gridApi}
                    gridColumnApi={gridColumnApi}
                    customComponents={customComponents}
                    filter={filter}
                    handleChangeFilter={handleChangeFilter}
                />

                {!sourceSet || isFetching ? <LinearProgress /> : <Divider />}

                {sourceSet && isAnalyticsEnabled && (
                    <AnalyticsGrid
                        sourceSet={sourceSet}
                        sourceSetModel={sourceSetModel}
                        quickFilterValue={filter}
                        setGridApi={(api, colApi) => {
                            setGridApi(api);
                            setGridColumnApi(colApi);
                        }}
                    />
                )}
                {sourceSet && isChart && !isAnalyticsEnabled && (
                    <SourceSetGridChart
                        key={sourceSet.id + layout}
                        sourceSet={sourceSet}
                        creatorLevel={creatorLevel}
                        sourceSetModel={sourceSetModel}
                        selectedSourceSetElementId={selectedSourceSetElementId}
                    />
                )}
                {sourceSet && isChart === false && !isAnalyticsEnabled && (
                    <SourceSetGrid
                        key={sourceSet.id}
                        sourceSet={sourceSet}
                        setGridApi={(api, colApi) => {
                            setGridApi(api);
                            setGridColumnApi(colApi);
                        }}
                        sourceSetModel={sourceSetModel}
                        selectedSourceSetElementId={selectedSourceSetElementId}
                        cellInteractionHandler={cellInteractionHandler}
                        quickFilterValue={filter}
                    />
                )}
                <EditOverlay />
            </div>
        </Paper>
    );
};

export default DataGridPane;
